import React, { useState, useEffect, useMemo } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { Review, GroupedReview } from "@/types/review";
import ReviewMap from "@/components/reviews/ReviewMap";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/context/LanguageContext";
import AddressReviewCard from "@/components/reviews/AddressReviewCard";
import { FilterOptions } from "@/components/reviews/list/FilterPanel";
import { SortOption } from "@/components/reviews/list/SortOptions";
import ActionButtons from "@/components/reviews/list/ActionButtons";
import { filterReviewsBySearch } from "@/utils/searchUtils";

// Helper function to create consistent location key based on coordinates
function createLocationKey(lat: number, lng: number): string {
  return `${Number(lat).toFixed(4)}-${Number(lng).toFixed(4)}`;
}

// Helper to determine if a review shows easy application
function hasEasyApply(review: Review): boolean {
  return !(
    review.requires_employment_proof || 
    review.requires_credit_check || 
    review.has_income_requirement || 
    review.requires_financial_proof
  );
}

// Helper to determine if a review shows flexible contract
function hasFlexibleContract(review: Review): boolean {
  return Boolean(
    review.allows_daily_rental || 
    review.allows_monthly_rental || 
    review.allows_semiannual_rental || 
    review.allows_contract_termination || 
    review.allows_contract_transfer
  );
}

const Reviews = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showMap, setShowMap] = useState<boolean>(false);
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  
  // State for sorting, filtering and UI controls
  const [sortOption, setSortOption] = useState<SortOption>("rating-high");
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterOptions>({
    utilities: [],
    bedrooms: [],
    bathrooms: [],
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    utilities: [],
    bedrooms: [],
    bathrooms: [],
  });
  
  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.petAllowed) count++;
    if (appliedFilters.immigrantFriendly) count++;
    if (appliedFilters.utilities.length > 0) count++;
    if (appliedFilters.bedrooms.length > 0) count++;
    if (appliedFilters.bathrooms.length > 0) count++;
    return count;
  }, [appliedFilters]);
  
  // Apply filters function
  const applyFilters = () => {
    setAppliedFilters({...filters});
  };
  
  // Reset filters function
  const resetFilters = () => {
    setFilters({
      utilities: [],
      bedrooms: [],
      bathrooms: []
    });
    setAppliedFilters({
      utilities: [],
      bedrooms: [],
      bathrooms: []
    });
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };
  
  // Toggle map view
  const toggleMapView = () => {
    setShowMap(!showMap);
  };

  // 添加防呆邏輯：強制在 10 秒後停止 loading
  useEffect(() => {
    const maxLoadingTime = setTimeout(() => {
      if (loading) {
        console.log("Reviews: Force stopping loading after 10 seconds");
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(maxLoadingTime);
  }, [loading]);

  useEffect(() => {
    const fetchReviews = async () => {
      console.log("Reviews: Starting to fetch reviews");
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .order('created_at', { ascending: false });
        
        console.log("Reviews: Database query completed", { data: data?.length, error });
        
        if (error) {
          throw error;
        }

        if (data) {
          console.log("Reviews: Transforming reviews data", data.length);
          const transformedReviews = data.map(item => {
            let coordinates = { lat: 51.0447, lng: -114.0719 };
            
            if (item.coordinates) {
              try {
                if (typeof item.coordinates === 'object') {
                  const coords = item.coordinates as Record<string, any>;
                  if ('lat' in coords && 'lng' in coords) {
                    const lat = Number(coords.lat);
                    const lng = Number(coords.lng);
                    
                    if (!isNaN(lat) && !isNaN(lng)) {
                      coordinates = { lat, lng };
                    }
                  }
                }
              } catch (e) {
                console.error(`Error parsing coordinates for review ${item.id}:`, e);
              }
            }
            
            return {
              ...item,
              coordinates,
              pet_allowed: item.pet_allowed || false,
              utilities_included: item.utilities_included || [],
              bedrooms: item.bedrooms || 0,
              living_rooms: item.living_rooms || 0,
              bathrooms: item.bathrooms || 0,
              deposit_type: item.deposit_type as "amount" | "months",
              rental_status: item.rental_status as "viewed" | "renting" | "ended",
              image_urls: item.image_urls || [],
              housing_type: item.housing_type as "house" | "townhouse" | "apartment" | "condo" || undefined
            } as Review;
          });
          
          console.log("Reviews: Setting transformed reviews", transformedReviews.length);
          setReviews(transformedReviews);
        }
      } catch (error: any) {
        console.error("Error fetching reviews:", error);
        toast({
          variant: "destructive",
          title: t('errorFetchingReviews'),
          description: error.message,
        });
      } finally {
        console.log("Reviews: Setting loading to false");
        setLoading(false);
      }
    };

    fetchReviews();
  }, [toast, t]);

  const filteredReviews = useMemo(() => {
    let result = [...reviews];

    if (ratingFilter !== "all") {
      result = result.filter(review => {
        if (ratingFilter === "high") return review.rating >= 4;
        if (ratingFilter === "mid") return review.rating === 3;
        if (ratingFilter === "low") return review.rating <= 2;
        return true;
      });
    }

    // 使用新的搜尋工具來支援中英文搜尋
    if (searchQuery.trim()) {
      result = filterReviewsBySearch(result, searchQuery);
    }
    
    // Apply custom filters
    if (appliedFilters.petAllowed) {
      result = result.filter(review => review.pet_allowed === true);
    }
    
    if (appliedFilters.immigrantFriendly) {
      result = result.filter(review => review.immigrant_friendly === true);
    }
    
    if (appliedFilters.utilities.length > 0) {
      result = result.filter(review => 
        appliedFilters.utilities.every(utility => 
          review.utilities_included?.includes(utility)
        )
      );
    }
    
    if (appliedFilters.bedrooms.length > 0) {
      result = result.filter(review => {
        if (review.bedrooms >= 3 && appliedFilters.bedrooms.includes(3)) {
          return true;
        }
        return appliedFilters.bedrooms.includes(review.bedrooms);
      });
    }
    
    if (appliedFilters.bathrooms.length > 0) {
      result = result.filter(review => {
        if (review.bathrooms >= 3 && appliedFilters.bathrooms.includes(3)) {
          return true;
        }
        return appliedFilters.bathrooms.includes(review.bathrooms);
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      switch (sortOption) {
        case 'rating-high':
          return b.rating - a.rating;
        case 'rating-low':
          return a.rating - b.rating;
        case 'rent-high':
          return Number(b.rent) - Number(a.rent);
        case 'rent-low':
          return Number(a.rent) - Number(b.rent);
        default:
          return 0;
      }
    });

    return result;
  }, [reviews, ratingFilter, searchQuery, sortOption, appliedFilters]);

  const groupedReviews = useMemo(() => {
    const displayedReviews = filteredReviews;
    const groupedMap = new Map<string, Review[]>();
    
    displayedReviews.forEach(review => {
      if (!review.coordinates || !review.coordinates.lat || !review.coordinates.lng) return;
      
      const locationKey = createLocationKey(review.coordinates.lat, review.coordinates.lng);
      const existingReviews = groupedMap.get(locationKey) || [];
      groupedMap.set(locationKey, [...existingReviews, review]);
    });
    
    return Array.from(groupedMap.entries()).map(([locationKey, reviews]) => {
      const centerLat = reviews.reduce((sum, review) => sum + review.coordinates.lat, 0) / reviews.length;
      const centerLng = reviews.reduce((sum, review) => sum + review.coordinates.lng, 0) / reviews.length;
      
      // Find community name or most common address with privacy handling
      let communityName = reviews.find(r => r.community_name)?.community_name;
      
      const addressCounts = new Map<string, number>();
      const displayAddressCounts = new Map<string, number>();
      
      reviews.forEach(review => {
        // Prioritize display_address for privacy, fallback to address_detail
        const addressToUse = review.display_address || review.address_detail;
        if (addressToUse) {
          const count = addressCounts.get(addressToUse) || 0;
          addressCounts.set(addressToUse, count + 1);
        }
        
        // Also track display addresses separately
        if (review.display_address) {
          const count = displayAddressCounts.get(review.display_address) || 0;
          displayAddressCounts.set(review.display_address, count + 1);
        }
      });
      
      let mostCommonAddress = reviews[0].display_address || reviews[0].address_detail;
      let mostCommonDisplayAddress = reviews[0].display_address;
      let maxCount = 0;
      
      addressCounts.forEach((count, address) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommonAddress = address;
        }
      });
      
      // Find most common display address
      let maxDisplayCount = 0;
      displayAddressCounts.forEach((count, address) => {
        if (count > maxDisplayCount) {
          maxDisplayCount = count;
          mostCommonDisplayAddress = address;
        }
      });
      
      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = totalRating / reviews.length;
      
      const rents = reviews.map(r => Number(r.rent) || 0).filter(r => r > 0);
      const minRent = rents.length > 0 ? Math.min(...rents) : 0;
      const maxRent = rents.length > 0 ? Math.max(...rents) : 0;
      
      const deposits = reviews.map(r => {
        if (r.deposit_type === "amount") {
          return Number(r.deposit_value) || 0;
        } else {
          const rent = Number(r.rent) || 0;
          const months = Number(r.deposit_value) || 0;
          return rent * months;
        }
      }).filter(d => d > 0);
      const minDeposit = deposits.length > 0 ? Math.min(...deposits) : 0;
      const maxDeposit = deposits.length > 0 ? Math.max(...deposits) : 0;
      
      // Get bedroom and bathroom counts
      const bedrooms = reviews.map(r => r.bedrooms || 0).filter(b => b > 0);
      const minBedrooms = bedrooms.length > 0 ? Math.min(...bedrooms) : 0;
      const maxBedrooms = bedrooms.length > 0 ? Math.max(...bedrooms) : 0;
      
      const bathrooms = reviews.map(r => r.bathrooms || 0).filter(b => b > 0);
      const minBathrooms = bathrooms.length > 0 ? Math.min(...bathrooms) : 0;
      const maxBathrooms = bathrooms.length > 0 ? Math.max(...bathrooms) : 0;

      const rentalPeriods = reviews
        .map(r => Number(r.min_rental_period) || 0)
        .filter(period => period > 0);
      const minRentalPeriod = rentalPeriods.length > 0 ? Math.min(...rentalPeriods) : 0;
      
      // Check for pet and immigrant friendly
      const hasPetAllowed = reviews.some(r => r.pet_allowed === true);
      const hasImmigrantFriendly = reviews.some(r => 
        r.immigrant_friendly === true || 
        r.accepts_overseas_proof_of_funds === true || 
        r.accepts_overseas_employment_proof === true
      );
      
      // Collect housing types
      const housingTypes = [...new Set(reviews
        .map(r => r.housing_type)
        .filter(type => type) as string[]
      )];
      
      // Check for easy application and flexible contract
      const hasEasyApplyFlag = reviews.some(r => hasEasyApply(r));
      const hasFlexibleContractFlag = reviews.some(r => hasFlexibleContract(r));
      
      // Collect utilities
      const allUtilities = [...new Set(reviews.flatMap(r => r.utilities_included || []))];
      
      // Find the first review with an image to use as the group's image
      const firstReviewWithImage = reviews.find(review => 
        review.image_urls && review.image_urls.length > 0
      );
      
      const firstImageUrl = firstReviewWithImage?.image_urls?.[0];
      
      // Get the latest review date
      const lastReviewDate = reviews
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at;
      
      // Check for parking availability - updated to use parking_information
      const hasParkingAvailable = reviews.some(r => 
        r.parking_information && r.parking_information.length > 0
      );
      
      return {
        locationKey,
        communityName,
        address: mostCommonAddress,
        display_address: mostCommonDisplayAddress, // Add display_address for privacy
        coordinates: {
          lat: centerLat,
          lng: centerLng
        },
        averageRating,
        reviews,
        totalReviews: reviews.length,
        minRent,
        maxRent,
        minDeposit,
        maxDeposit,
        minBedrooms,
        maxBedrooms,
        minBathrooms,
        maxBathrooms,
        allUtilities,
        minRentalPeriod,
        hasPetAllowed,
        hasImmigrantFriendly,
        firstImageUrl,
        housingTypes,
        hasEasyApply: hasEasyApplyFlag,
        hasFlexibleContract: hasFlexibleContractFlag,
        lastReviewDate,
        hasParkingAvailable
      } as GroupedReview;
    });
  }, [filteredReviews]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInputValue);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchQuery("");
  };

  const handleSortChange = (option: SortOption) => {
    setSortOption(option);
  };

  return (
    <MainLayout>
      <div className="container py-4 md:py-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 px-4 md:px-0">
          {t('rental.experiences')}
        </h1>

        <div className="space-y-4 px-4 md:px-0">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
            <Input
              placeholder={t('reviews.searchAddress')}
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              className="w-full"
            />
            <div className="flex gap-2">
              <Button type="submit" className="w-full md:w-auto">
                <Search className="h-4 w-4 mr-2" />
                {t('reviews.search')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full md:w-auto"
                onClick={handleClearSearch}
              >
                <X className="h-4 w-4 mr-2" />
                {t('clear')}
              </Button>
            </div>
          </form>

          {/* Action Buttons */}
          <ActionButtons
            sortOption={sortOption}
            onSortChange={handleSortChange}
            onToggleFilters={toggleFilters}
            onToggleMapView={toggleMapView}
            showMap={showMap}
            isFiltersOpen={isFiltersOpen}
            filters={filters}
            setFilters={setFilters}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            activeFilterCount={activeFilterCount}
          />
        </div>

        <div className="mb-4 text-sm text-muted-foreground px-4 md:px-0">
          {loading ? (
            t('reviews.loading')
          ) : (
            groupedReviews.length > 0 ? (
              t('reviews.foundAddresses', { count: groupedReviews.length, reviews: filteredReviews.length })
            ) : (
              t('noResultsMatchingFilters')
            )
          )}
        </div>

        <div className="px-4 md:px-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-muted-foreground">{t('reviews.loadingReviews')}</p>
            </div>
          ) : (
            <>
              {showMap && (
                <div className="mb-4">
                  <ReviewMap 
                    reviews={filteredReviews} 
                    height="400px"
                    searchQuery={searchQuery}
                    groupedReviews={groupedReviews}
                  />
                </div>
              )}

              <div className="grid gap-6 mb-6">
                {groupedReviews.length > 0 ? (
                  groupedReviews.map((groupedReview) => (
                    <AddressReviewCard
                      key={groupedReview.locationKey}
                      groupedReview={groupedReview}
                    />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">{t('noResultsMatchingFilters')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Reviews;
